// EvaluationRichClient.jsx - Gestion des évaluations enrichies avec pagination et filtres
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import DemoViewer from '../../components/communs/DemoViewer';

import {
  ClipboardList, Eye, Clock, CheckCircle,
  XCircle, AlertCircle, Loader2, RefreshCw,
  Calendar, User, Award, BookOpen, ChevronLeft, ChevronRight,
  Users, TrendingUp
} from 'lucide-react';

export default function EvaluationRichClient() {
  const { token, isAuthenticated } = useAuth();

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Pagination et filtres
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    type: '',
    statut: ''
  });

  const [showEvalDemo, setShowEvalDemo] = useState(false);
  const [demoEvalData, setDemoEvalData] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // État pour suivre le changement de statut par évaluation
  const [changingStatus, setChangingStatus] = useState({});

  const fetchEvaluations = useCallback(async (page = 0, type = '', statut = '') => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('size', pagination.size);
      if (type) params.set('type', type);
      if (statut) params.set('statut', statut);

      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/rich/evaluations?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
      );
      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
      const data = await response.json();
      const evalList = data.content || data.data || data || [];
      setEvaluations(evalList);
      setPagination(prev => ({
        ...prev,
        page: data.number || page,
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements || evalList.length
      }));
    } catch (err) {
      console.error('Erreur récupération évaluations enrichies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, isAuthenticated, pagination.size]);

  useEffect(() => {
    fetchEvaluations(0, filters.type, filters.statut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    await fetchEvaluations(pagination.page, filters.type, filters.statut);
  }, [fetchEvaluations, pagination.page, filters.type, filters.statut]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      fetchEvaluations(newPage, filters.type, filters.statut);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    fetchEvaluations(0, key === 'type' ? value : filters.type, key === 'statut' ? value : filters.statut);
  };

  // Changement de statut d'une évaluation enrichie
  const changeStatus = async (evaluationId, newStatus) => {
    setChangingStatus(prev => ({ ...prev, [evaluationId]: true }));
    try {
      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/rich/evaluations/${evaluationId}/statut?statut=${newStatus}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
      if (response.ok) {
        await refreshData();
      } else {
        const errorData = await response.json();
        alert(`Erreur: ${errorData.message || 'Changement de statut échoué'}`);
      }
    } catch (err) {
      console.error('Erreur changement de statut:', err);
      alert('Erreur réseau');
    } finally {
      setChangingStatus(prev => ({ ...prev, [evaluationId]: false }));
    }
  };

  const openEvalDemo = useCallback(async (evaluation) => {
    setLoadingQuestions(true);
    try {
      const questions = evaluation.questions || [];
      if (questions.length === 0) {
        alert("⚠️ Cette évaluation enrichie ne contient aucune question.");
        setLoadingQuestions(false);
        return;
      }
      const demoData = {
        isDirectEvaluation: true,
        isEvaluationRich: true,
        formationId: null,
        module: {
          id: evaluation.id || 'eval-rich-demo',
          titre: evaluation.titre || 'Évaluation enrichie sans titre',
          description: evaluation.description || '',
          type: 'EVALUATION_RICH',
          typeModule: 'EVALUATION_RICH',
          ordre: 0,
          formationId: null,
          evalType: evaluation.type || 'SOMMATIVE',
          questions: questions,
          content: questions
        },
        content: {
          type: 'EVALUATION_RICH',
          data: {
            id: evaluation.id,
            titre: evaluation.titre,
            description: evaluation.description,
            type: evaluation.type || 'SOMMATIVE',
            questions: questions,
            scoreMinimum: evaluation.scoreMinimum || 70
          },
          questions: questions,
          evaluations: questions
        },
        token,
        mode: 'demo'
      };
      setDemoEvalData(demoData);
      setShowEvalDemo(true);
      setLoadingQuestions(false);
    } catch (err) {
      console.error('Erreur ouverture démo:', err);
      alert("❌ Erreur lors du chargement des questions: " + err.message);
      setLoadingQuestions(false);
    }
  }, [token]);

  const getStatusInfo = (statut) => {
    const map = {
      'BROUILLON': { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
      'PUBLIEE': { label: 'Publiée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'EN_COURS': { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
      'TERMINE': { label: 'Terminée', color: 'bg-green-100 text-green-700', icon: CheckCircle }
    };
    return map[statut?.toUpperCase()] || { label: statut || 'Inconnu', color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
  };

  const getTypeInfo = (type) => {
    const map = {
      'SOMMATIVE': { label: 'Sommatif', color: 'bg-purple-100 text-purple-700' },
      'FORMATIVE': { label: 'Formatif', color: 'bg-blue-100 text-blue-700' },
      'DIAGNOSTIC': { label: 'Diagnostique', color: 'bg-emerald-100 text-emerald-700' }
    };
    return map[type?.toUpperCase()] || { label: type || 'Inconnu', color: 'bg-gray-100 text-gray-700' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-8 text-center">
        <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button onClick={refreshData} className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">
          <RefreshCw size={16} className="inline mr-2" /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-purple-200 shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList size={20} className="text-purple-600" />
            <h3 className="text-sm font-bold text-slate-700">
              Évaluations enrichies
              <span className="ml-2 text-xs font-normal text-slate-400">
                ({pagination.totalElements || evaluations.length} évaluation{pagination.totalElements > 1 ? 's' : ''})
              </span>
            </h3>
          </div>
          <button onClick={refreshData} disabled={refreshing} className="px-3 py-1.5 text-xs bg-purple-50 text-purple-600 hover:bg-purple-100 rounded-lg transition flex items-center gap-1.5">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Rafraîchir
          </button>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-purple-100">
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="text-xs border border-purple-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white"
          >
            <option value="">Tous les types</option>
            <option value="SOMMATIVE">Sommatif</option>
            <option value="FORMATIVE">Formatif</option>
            <option value="DIAGNOSTIC">Diagnostique</option>
          </select>
          <select
            value={filters.statut}
            onChange={(e) => handleFilterChange('statut', e.target.value)}
            className="text-xs border border-purple-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white"
          >
            <option value="">Tous les statuts</option>
            <option value="BROUILLON">Brouillon</option>
            <option value="PUBLIEE">Publiée</option>
            <option value="EN_COURS">En cours</option>
            <option value="TERMINE">Terminée</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-purple-200 shadow-sm overflow-hidden">
        {evaluations.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList size={48} className="mx-auto text-purple-300 mb-3" />
            <p className="text-purple-500 font-medium">Aucune évaluation enrichie</p>
            <p className="text-xs text-slate-400 mt-1">Ajustez vos filtres ou créez une évaluation enrichie.</p>
          </div>
        ) : (
          <div className="divide-y divide-purple-100">
            {evaluations.map((evalItem) => {
              const statusInfo = getStatusInfo(evalItem.status);
              const typeInfo = getTypeInfo(evalItem.type);
              const StatusIcon = statusInfo.icon;
              const hasQuestions = (evalItem.questions?.length || 0) > 0;
              const isChanging = changingStatus[evalItem.id] || false;

              return (
                <div key={evalItem.id} className="px-4 py-3 hover:bg-purple-50 transition flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{evalItem.titre || 'Sans titre'}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${typeInfo.color}`}>{typeInfo.label}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${statusInfo.color} flex items-center gap-1`}>
                        <StatusIcon size={10} /> {statusInfo.label}
                      </span>
                      {!hasQuestions && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">⚠️ Sans questions</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400 flex-wrap">
                      {evalItem.code && <span className="font-mono">{evalItem.code}</span>}
                      {evalItem.scoreMinimum !== undefined && <span className="flex items-center gap-1 text-purple-600">Seuil: {evalItem.scoreMinimum}%</span>}
                      {evalItem.dureeMinutes && <span className="flex items-center gap-1"><Clock size={11} /> {evalItem.dureeMinutes} min</span>}
                      {/* Statistiques */}
                      {evalItem.participants !== undefined && (
                        <span className="flex items-center gap-1">
                          <Users size={11} /> {evalItem.participants} participant(s)
                        </span>
                      )}
                      {evalItem.noteMoyenne !== undefined && (
                        <span className="flex items-center gap-1 text-emerald-600 font-bold">
                          <Award size={11} /> {evalItem.noteMoyenne.toFixed(1)}/100
                        </span>
                      )}
                      {evalItem.tauxParticipation !== undefined && (
                        <span className="flex items-center gap-1">
                          <TrendingUp size={11} /> {evalItem.tauxParticipation}% participation
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Sélecteur de statut */}
                    <select
                      value={evalItem.status || 'BROUILLON'}
                      onChange={(e) => changeStatus(evalItem.id, e.target.value)}
                      disabled={isChanging}
                      className="text-[10px] border border-purple-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white disabled:opacity-50"
                    >
                      <option value="BROUILLON">Brouillon</option>
                      <option value="PUBLIEE">Publiée</option>
                      <option value="TERMINEE">Terminée</option>
                    </select>
                    {/* Prévisualisation */}
                    <button
                      onClick={() => hasQuestions && openEvalDemo(evalItem)}
                      disabled={!hasQuestions || loadingQuestions}
                      className={`p-2 rounded-lg transition ${hasQuestions && !loadingQuestions ? 'text-purple-600 hover:bg-purple-50' : 'text-gray-300 cursor-not-allowed'}`}
                      title={hasQuestions ? "Prévisualiser" : "Aucune question"}
                    >
                      {loadingQuestions ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
          <span>
            Page {pagination.page + 1} / {pagination.totalPages} ({pagination.totalElements} éléments)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 0}
              className="p-1.5 rounded border border-purple-200 hover:bg-purple-100 disabled:opacity-30 transition"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages - 1}
              className="p-1.5 rounded border border-purple-200 hover:bg-purple-100 disabled:opacity-30 transition"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Modal de démo */}
      {showEvalDemo && demoEvalData && (
        <DemoViewer
          formationId={null}
          token={demoEvalData.token || token}
          onClose={() => { setShowEvalDemo(false); setDemoEvalData(null); }}
          isOpen={showEvalDemo}
          userRole="client"
          initialModule={demoEvalData.module}
          initialContent={demoEvalData.content}
          isDirectEvaluation={true}
          isEvaluationRich={true}
        />
      )}
    </div>
  );
}